/**
 * Every server-state query key, in one place, so an invalidation names exactly what a read cached.
 *
 * A key is `[feature, resource, ...every parameter that changes the answer]`. A parameter left out of the key is a
 * stale answer waiting to be drawn for the wrong input. The selected entity is not in the keys: `QueryProvider`
 * resets the cache when it changes.
 */
export const queryKeys = {
  masters: {
    all: ['masters'] as const,
    activity: (master: string, recordId: number) => ['masters', master, 'activity', recordId] as const,
  },
  templates: {
    all: ['templates'] as const,
    activity: (templateId: number) => ['templates', 'activity', templateId] as const,
  },
  purchaseRequests: {
    all: ['purchase-requests'] as const,
    usage: (requestId: number) => ['purchase-requests', 'usage', requestId] as const,
  },
  rfqs: {
    all: ['rfqs'] as const,
    supplierDirectory: (search: string) => ['rfqs', 'supplier-directory', search] as const,
    products: (search: string, category: string | null) => ['rfqs', 'products', search, category] as const,
  },
  /* AI Control (2026-10-30): the categories, and one category's weightage. */
  aiControl: {
    all: ['ai-control'] as const,
    categories: () => ['ai-control', 'categories'] as const,
    category: (categoryId: number) => ['ai-control', 'category', categoryId] as const,
  },
  /*
    The notification bell (2026-10-29). Counts are their own key because they outlive any one tab: the badge
    polls them while the panel is shut, and every tab's label reads from the same answer.
  */
  notifications: {
    all: ['notifications'] as const,
    counts: () => ['notifications', 'counts'] as const,
    /* The tab is in the key: each tab is a different question to the server, not a filter over one answer. */
    list: (tab: string) => ['notifications', 'list', tab] as const,
  },
  /* The Supplier Bid Quotation (2026-10-28): the supplier's BIDs, and one BID with its quotation. */
  supplierQuotation: {
    all: ['supplier-quotation'] as const,
    /* The list, and each column sort of it (2026-10-29); invalidating `bids()` refreshes every sort. */
    bids: (sort?: string) => (sort ? (['supplier-quotation', 'bids', sort] as const) : (['supplier-quotation', 'bids'] as const)),
    bid: (rfqId: number) => ['supplier-quotation', 'bid', rfqId] as const,
  },
  /* The Supplier Dashboard (2026-10-29, NB-637): its KPI cards. The BID Calendar shares `supplierQuotation.bids()`. */
  supplierDashboard: {
    summary: ['supplier-dashboard', 'summary'] as const,
  },
} as const;
